/** @module store/user */

import { DEFAULT_LOCALE } from '@/i18n'
import * as apiUsers from '@/api/users'
import { EMS_PERMISSION_TYPES, ORG_PERMISSION_TYPES, PERMISSION_FLAGS, Permission } from '@/store/modules/users'

/**
 * Store state for current `user`.
 *
 * - `{integer|string} id` is the MPG User ID
 * - `{string} email` is currently NOT used
 * - `{object} settings` are various User settings
 * - `{object} permission` is a user's [Permission]{@link module:store/users.Permission} object specifying the (aggregated) permission the current user is granted by her group-membership(s)
 * - `{integer[]} groupIds` is the list of goup IDs from which the current user's permission were granted
 *
 * @function
 *
 * @return {object}
 */
const state = () => ({
  id: null,
  email: '',
  roles: [],
  settings: {
    datetimeLocale: '',
    isDarkTheme: false,
    locale: ''
  },
  permission: {},
  groupIds: []
})

const getters = {
  id: (state) => {
    return state.id
  },
  isDarkTheme: (state) => {
    return state.settings.isDarkTheme
  },
  isTester: (state) => {
    return state.roles.includes('tester')
  },
  isOrgSudo: (state) => {
    return state.roles.includes('org_sudo')
  },
  isSudo: (state) => {
    return state.roles.includes('sudo')
  },
  locale: (state) => {
    return state.settings.locale || DEFAULT_LOCALE
  },
  datetimeLocale: (state) => {
    return state.settings.datetimeLocale || DEFAULT_LOCALE
  },
  hasPermission: (state) => {
    return ({ role, type }) => {
      if (!EMS_PERMISSION_TYPES.includes(type) && !ORG_PERMISSION_TYPES.includes(type)) {
        throw new TypeError(`The permission type ${type} is not supported`)
      }

      if (!state.permission[role]) {
        return false
      }
      return (state.permission[role] & PERMISSION_FLAGS[type]) > 0
    }
  },
  roles: (state) => {
    return state.roles
  }
}

const mutations = {
  SET_ID(state, id) {
    state.id = id
  },
  SET_ROLES(state, roles) {
    if (typeof roles === 'string') {
      state.roles = roles.split(',')
    } else if (Array.isArray(roles)) {
      state.roles = roles
    } else {
      state.roles = []
    }
  },
  SET_LOCALE(state, locale) {
    state.settings.locale = locale
  },
  SET_DATETIME_LOCALE(state, locale) {
    state.settings.datetimeLocale = locale
  },
  SET_PERMISSION(state, permission) {
    state.permission = permission
  },
  SET_GROUP_IDS(state, groupIds) {
    state.groupIds = groupIds
  },
  SET_IS_DARK_THEME(state, isDarkTheme) {
    state.settings.isDarkTheme = isDarkTheme
  }
}

/**
 * Action to get the current user.
 * Data are looked up in the HTML (might get an AJAX in the future).
 *
 * @function
 *
 * @return {object} the user
 */
async function getUser({ commit }) {
  const u = document.getElementById('main').dataset
  if (!u.userId) {
    throw new Error('Failed to get user')
  }

  commit('SET_ID', u.userId)
  commit('SET_LOCALE', u.userLocale)
  commit('SET_DATETIME_LOCALE', u.userDatetimeLocale)
  commit('SET_ROLES', u.userRoles)

  return u
}

function trackDarkTheme({ commit }) {
  const dark = document.body.getAttribute('data-coreui-theme') === 'dark'
  commit('SET_IS_DARK_THEME', dark)

  document.body.addEventListener('classtoggle', (e) => {
    commit('SET_IS_DARK_THEME', e.detail.theme === 'dark')
  })
}

async function getUserPermissionEms({ state, commit }, { userId = null, emsId, ctxt, membership = null }) {
  if (!userId) {
    userId = state.id
  }

  const rsp = await apiUsers.getUserPermissionEms({
    id: userId,
    emsId,
    ctxt,
    membership
  })

  commit('SET_PERMISSION', new Permission(rsp.payload.data.attributes.permission))
  commit('SET_GROUP_IDS', rsp.payload.data.attributes.groupIds)
}

async function getUserPermissionGroup(
  { state, commit },
  { userId = null, groupId, ctxt, membership = null, mode = null }
) {
  if (!userId) {
    userId = state.id
  }

  const rsp = await apiUsers.getUserPermissionGroup({
    id: userId,
    groupId,
    ctxt,
    membership,
    mode
  })

  commit('SET_PERMISSION', new Permission(rsp.payload.data.attributes.permission))
  commit('SET_GROUP_IDS', rsp.payload.data.attributes.groupIds)
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    getUser,
    getUserPermissionEms,
    getUserPermissionGroup,
    trackDarkTheme
  }
}
